import React from "react";
import CustomTabContainer from "../CustomTabContainer";
import CustomTab from "../CustomTab";
import ButtonAction from "../buttons/ButtonAction";
import { TrashOutlineIcon } from "../buttons/IconButtons";
import { SEARCH_MODEL } from "../table/Model/TableModel";
import AddTabResource from "./SubComponents/AddTabResource";
import AssignedTabResource from "./SubComponents/AssignedTabResource";
import { TabPanelApp } from "./Functionality/TabPanelApp";
import { TAB_PANEL_MODEL } from "./Model/TabPanelModel";

/**
 * Author: Arnulfo Ferro Zapata
 * Job Title: Fullstack
 * Email: azpata@ibl.mx
 *
 * Generic Add/Assigned tab panel for a shift-resource section. Top header with a
 * bulk-delete action on the Assigned tab, then the Add (catalog search) or
 * Assigned (local selection) view. Domain-agnostic and reused across modules:
 * each caller binds its resource config through the `useAddApp` / `useAssignedApp`
 * hooks and the tab definitions (`tabsConfig`).
 *
 * @param {Object} props
 * @param {string} props.resource - Resource type handled by this section.
 * @param {Object} props.tabsConfig - Tab definitions, exposing getTabsById(id).
 * @param {Array<Object>} props.selected - Section selection list.
 * @param {Function} props.setSelected - Section selection setter.
 * @param {Array} props.selectForDelete - Delete buffer for the Assigned tab.
 * @param {Function} props.setSelectForDelete - Delete buffer setter.
 * @param {number|string|null|undefined} props.id - Shift id (edit mode).
 * @param {string} props.deleteButtonId - DOM id for the bulk-delete button.
 * @param {Function} props.useAddApp - Domain hook for the Add tab config.
 * @param {Function} props.useAssignedApp - Domain hook for the Assigned tab config + handlers.
 * @param {boolean} props.emptyFields - When true, flags the Add tab selected section red (empty required selection on next/update).
 */
const TabPanel = ({
                           resource,
                           tabsConfig,
                           selected,
                           setSelected,
                           selectForDelete,
                           setSelectForDelete,
                           searchElement,
                           setSearchElement,
                           modified,
                           setModified,
                           id,
                           deleteButtonId,
                           useAddApp,
                           useAssignedApp,
                           mainContainerClass='',
                           emptyFields=false
                       }) => {

    const {
        t,
        subViewChild, setSubViewChild,
        results, setResults,
        setBodyForExport,
        setSearch,
        setSimpleSearch,
        triggerDelete, setTriggerDelete,
        isAllChecked, setIsAllChecked,
    } = TabPanelApp(TAB_PANEL_MODEL.ADD);

    const tabs = tabsConfig.getTabsById(id);
    const addTab = tabs[TAB_PANEL_MODEL.ADD];
    const assignedTab = tabs[TAB_PANEL_MODEL.ASSIGNED];

    return (
        <div className={`col-12 border-radius-8px px-0 ${mainContainerClass}`}>
            { id != null &&
                <>
                    <CustomTabContainer
                        activeTab={subViewChild}
                        setActiveTab={(e) => {
                            setSearchElement("");
                            setResults(SEARCH_MODEL.clearResults());
                            setSelectForDelete([]);
                            setIsAllChecked(false);
                            setSubViewChild(e);
                        }}
                        className="border-0 px-3 d-flex justify-content-between"
                        paddingClass=''
                        buttonAction={
                            subViewChild === assignedTab?.key &&
                            <ButtonAction
                                id={deleteButtonId}
                                name='delete'
                                className={`btn-icon-delete h-100`}
                                Icon={TrashOutlineIcon}
                                disabled={selectForDelete.length <= 0}
                                onClick={() => setTriggerDelete(true)}
                            />
                        }
                    >
                        {tabs.map((tab) => (
                            <CustomTab
                                key={tab.key}
                                id={tab.key}
                                title={t(tab.translation)}
                                icon={tab.icon}
                                selected={subViewChild === tab.key}
                                onClick={() => setSubViewChild(tab.key)}
                            />
                        ))}
                    </CustomTabContainer>

                    <div className="border-bottom-shades-300 mb-3" />
                </>
            }
            {subViewChild === addTab?.key &&
                <AddTabResource
                    useAddApp={useAddApp}
                    resource={resource}
                    id={id}
                    setBodyForExport={setBodyForExport}
                    results={results}
                    setResults={setResults}
                    searchElement={searchElement}
                    setSearchElement={setSearchElement}
                    setSearch={setSearch}
                    setSimpleSearch={setSimpleSearch}
                    modified={modified}
                    setModified={setModified}
                    selected={selected?.filter(e => !e?.isAssigned)}
                    setSelected={setSelected}
                    emptyFields={emptyFields}
                />
            }

            {subViewChild === assignedTab?.key &&
                <AssignedTabResource
                    useAssignedApp={useAssignedApp}
                    resource={resource}
                    id={id}
                    selected={selected}
                    setSelected={setSelected}
                    selectForDelete={selectForDelete}
                    setSelectForDelete={setSelectForDelete}
                    isAllChecked={isAllChecked}
                    setIsAllChecked={setIsAllChecked}
                    triggerDelete={triggerDelete}
                    setTriggerDelete={setTriggerDelete}
                    searchElement={searchElement}
                    setSearchElement={setSearchElement}
                    results={results}
                    setResults={setResults}
                    setBodyForExport={setBodyForExport}
                    setSearch={setSearch}
                    setSimpleSearch={setSimpleSearch}
                    modified={modified}
                    setModified={setModified}
                />
            }
        </div>
    );
}

export default TabPanel
